import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, Send } from "lucide-react";
import { LOGO } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70";
const label = "block text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2";

const LEAVE_TYPES = ["Casual Leave", "Sick Leave", "Earned Leave", "Compensatory Off", "Maternity Leave", "Paternity Leave", "Bereavement Leave", "Special Leave", "Leave Without Pay"];

export default function LeaveRequest() {
  const [form, setForm] = useState({ email: "", leave_type: "Casual Leave", from_date: "", to_date: "", half_day: false, reason: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/leave/request`, form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your leave request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-nx-navy flex items-center justify-center px-6 py-16" data-testid="leave-request-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-10">
          <img src={LOGO.fullDark} alt="NexSecure Technologies" className="h-12 w-auto mx-auto mb-6" />
          <p className="text-[10px] tracking-[0.26em] uppercase text-nx-goldlight">Employee Self-Service</p>
          <h1 className="font-marcellus text-3xl text-white mt-3">Leave Request</h1>
          <p className="text-sm text-slate-500 mt-3">Apply for leave here. Your reporting manager and HR will review it, and you will get the decision by email.</p>
        </div>

        {done ? (
          <div className="border border-emerald-500/30 bg-emerald-500/10 p-8 text-center" data-testid="leave-success">
            <CheckCircle2 size={32} className="text-emerald-300 mx-auto mb-4" />
            <p className="text-white text-lg">Leave request submitted</p>
            <p className="text-sm text-slate-400 mt-2">Your manager and HR have been notified. You will receive an email with the decision.</p>
            <a href="/my-requests" className="inline-block mt-5 text-xs text-nx-goldlight hover:text-white transition-colors border border-nx-gold/40 px-4 py-2" data-testid="leave-track-link">Track this request anytime → My Requests</a>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] p-7 space-y-5">
            <label className="block">
              <span className={label}>Your email registered with HR</span>
              <input type="email" required className={input} value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="leave-email" />
            </label>
            <label className="block">
              <span className={label}>Leave type</span>
              <select className={input} value={form.leave_type} onChange={set("leave_type")} data-testid="leave-type">
                {LEAVE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className={label}>From date</span>
                <input type="date" required className={input} value={form.from_date} onChange={set("from_date")} data-testid="leave-from" />
              </label>
              <label className="block">
                <span className={label}>To date</span>
                <input type="date" required className={input} value={form.to_date} onChange={set("to_date")} data-testid="leave-to" />
              </label>
            </div>
            <label className="flex items-center gap-3 text-sm text-slate-300">
              <input type="checkbox" className="accent-[#d4af5e]" checked={form.half_day} onChange={set("half_day")} data-testid="leave-half-day" /> Half day (single day only)
            </label>
            <label className="block">
              <span className={label}>Reason</span>
              <textarea required minLength={5} rows={3} className={input} value={form.reason} onChange={set("reason")} placeholder="Brief reason for the leave" data-testid="leave-reason" />
            </label>
            <button type="submit" disabled={busy} className="w-full bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3.5 transition-colors disabled:opacity-50" data-testid="leave-submit">
              <Send size={13} className="inline mr-1" /> {busy ? "Submitting…" : "Submit Leave Request"}
            </button>
            <p className="text-[11px] text-slate-600 text-center"><CalendarDays size={11} className="inline mr-1" /> Apply in advance where possible; emergency leave can be regularized afterwards.</p>
          </form>
        )}
      </div>
    </div>
  );
}
